import { useEffect, useRef, useState } from "react";

/**
 * Post-call analytics — the panel that appears once a call ends and shows what the
 * platform extracted from it.
 *
 * Post-call analysis runs AFTER the call, asynchronously, so the values are not there the
 * moment the caller hangs up. This polls with a ceiling rather than fetching once, and says
 * plainly when it gave up instead of showing an empty card that looks like a failure.
 *
 * Read path (all three needed — the values arrive keyed by name with no labels or types):
 *   GET /conversations/{agentId}                                    the calls
 *   GET /agent-metric/properties/list?agent={agentId}-draft         field definitions
 *   GET /post-conversation-data/{cid}/pca-variables?agent_id={id}   extracted values
 */

const API_BASE = process.env.REACT_APP_NURIX_API_BASE ?? "https://agentx-us.nurixlabs.tech";

interface Props {
  agentId: string;
  workspaceId: string;
  /** Bumped when a call ends, so the panel restarts polling for the new conversation. */
  callEndedAt?: number | null;
}

interface Field { name: string; display: string; type: string }
type Values = Record<string, unknown>;

const headers = (workspaceId: string) => ({
  "Content-Type": "application/json",
  accept: "application/json",
  "workspace-id": workspaceId,
  "x-workspace-id": workspaceId,
  "user-id": "123",
});

/** Quality checks read as pass/fail; everything else is just a value to show. */
const BOOLEAN_LABELS: Record<string, [string, string]> = {
  true: ["Yes", "ok"], false: ["No", "bad"],
};
const QUALITY_HINT = /_once$|^no_|_respected$|_correct$|_used$|_won$|^verified$|_clean$|^offer/;

function prettify(name: string) {
  return name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function PostCallAnalytics({ agentId, workspaceId, callEndedAt }: Props) {
  const [fields, setFields] = useState<Field[]>([]);
  const [values, setValues] = useState<Values | null>(null);
  const [state, setState] = useState<"idle" | "waiting" | "ready" | "timeout">("idle");
  const timer = useRef<number | null>(null);

  // Field definitions are stable for the agent — fetch once.
  useEffect(() => {
    if (!agentId) return;
    let alive = true;
    fetch(`${API_BASE}/agent-metric/properties/list?agent=${encodeURIComponent(agentId)}-draft`, { headers: headers(workspaceId) })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        const list = j.properties ?? j.data ?? (Array.isArray(j) ? j : []);
        setFields(list.map((p: Record<string, string>) => ({
          name: p.name, display: p.display_name || prettify(p.name), type: (p.type || "STRING").toUpperCase(),
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [agentId, workspaceId]);

  useEffect(() => {
    if (!callEndedAt || !agentId) return;
    let alive = true;
    let tries = 0;
    setState("waiting"); setValues(null);

    const poll = async () => {
      tries += 1;
      try {
        const cr = await fetch(`${API_BASE}/conversations/${encodeURIComponent(agentId)}`, { headers: headers(workspaceId) });
        const cj = cr.ok ? await cr.json() : null;
        const list = Array.isArray(cj) ? cj : cj?.data ?? (cj ? [cj] : []);
        const cid = list[0]?.conversation_id ?? list[0]?.id;
        if (cid) {
          const pr = await fetch(
            `${API_BASE}/post-conversation-data/${encodeURIComponent(cid)}/pca-variables?agent_id=${encodeURIComponent(agentId)}`,
            { headers: headers(workspaceId) },
          );
          const pj = pr.ok ? await pr.json() : null;
          // The API nests extracted values under a couple of different keys depending on
          // how they were captured — merge rather than picking one and losing the rest.
          const merged: Values = { ...(pj?.runtime_variables ?? {}), ...(pj?.pca_variables ?? {}), ...(pj?.input_variables ?? {}) };
          if (alive && Object.keys(merged).length > 0) { setValues(merged); setState("ready"); return; }
        }
      } catch { /* transient — the retry below covers it */ }
      if (!alive) return;
      // ~90s ceiling. Analysis normally lands well inside that; saying so beats a
      // permanently empty card that reads as broken.
      if (tries >= 18) { setState("timeout"); return; }
      timer.current = window.setTimeout(poll, 5000);
    };

    timer.current = window.setTimeout(poll, 3000);
    return () => { alive = false; if (timer.current) window.clearTimeout(timer.current); };
  }, [callEndedAt, agentId, workspaceId]);

  if (!callEndedAt) return null;

  const shown = fields.length
    ? fields.filter((f) => values && values[f.name] !== undefined && values[f.name] !== null && values[f.name] !== "")
    : Object.keys(values ?? {}).map((n) => ({ name: n, display: prettify(n), type: "STRING" }));

  const quality = shown.filter((f) => QUALITY_HINT.test(f.name));
  const facts = shown.filter((f) => !QUALITY_HINT.test(f.name));

  const render = (f: Field) => {
    const raw = values?.[f.name];
    const s = String(raw);
    const isBool = f.type === "BOOLEAN" || s === "true" || s === "false";
    const [label, tone] = isBool ? BOOLEAN_LABELS[s] ?? [s, ""] : [s, ""];
    return (
      <div className="pca-row" key={f.name}>
        <span className="pca-k">{f.display}</span>
        <span className={`pca-v ${isBool ? `pca-bool ${tone}` : ""}`}>{label}</span>
      </div>
    );
  };

  return (
    <div className="ex-card pca-card">
      <style>{PCA_CSS}</style>
      <div className="ex-card-head">
        Post-call analysis
        <span className={`pca-pill ${state}`}>
          {state === "waiting" ? "analysing…" : state === "ready" ? `${shown.length} fields` : state === "timeout" ? "still processing" : ""}
        </span>
      </div>

      {state === "waiting" && (
        <p className="pca-note">The platform scores the call after it ends — this usually lands within a minute.</p>
      )}
      {state === "timeout" && (
        <p className="pca-note">Analysis has not come back yet. It runs asynchronously, so it may still appear in the console shortly.</p>
      )}

      {state === "ready" && (
        <>
          {facts.length > 0 && <div className="pca-group">{facts.map(render)}</div>}
          {quality.length > 0 && (
            <>
              <div className="pca-sub">Quality checks</div>
              <div className="pca-group">{quality.map(render)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const PCA_CSS = `
.pca-card { margin-top: 4px; }
.pca-pill { margin-left:auto; font-size:10px; font-weight:700; padding:3px 9px; border-radius:999px; background:#f1f5f9; color:#64748b; white-space:nowrap; }
.pca-pill.ready { background:#f0fdf4; color:#16a34a; }
.pca-pill.timeout { background:#fffbeb; color:#b45309; }
.pca-note { font-size:11.5px; line-height:1.55; color:#64748b; margin:-6px 0 2px; }
.pca-sub { font-size:10px; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:#94a3b8; margin:14px 0 6px; }
.pca-group { display:flex; flex-direction:column; }
.pca-row { display:grid; grid-template-columns:130px 1fr; gap:12px; align-items:baseline; padding:7px 0; border-bottom:1px solid #eef0f5; }
.pca-row:last-child { border-bottom:none; padding-bottom:0; }
.pca-k { font-size:11px; font-weight:700; color:#94a3b8; line-height:1.4; }
.pca-v { font-size:12.5px; font-weight:600; color:#0f172a; line-height:1.45; }
.pca-bool.ok { color:#16a34a; }
.pca-bool.bad { color:#dc2626; }
`;
